import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import DraggableIcon from "../components/teleport/DraggableIcon";
import ContentArea from "../components/content-area/Content";
import TeleportMapPng from "../assets/icons/map.png";
import TourGif from "../assets/gifs/teleport_tour.gif";
import { getTeleportTable, submitButtonPositionChange } from "../api/teleportApi";
import { getBooths } from "../api/boothApi";
import AddTeleportButtonModal from "../components/teleport/AddTeleportButtonModal";
import { withdrawApproval } from "../api/approvalApi";
import BoothSpot from "../components/teleport/BoothSpot";
import { useLocalization } from "../context/LocalizationContext";
import ConfirmModal from '../components/form-components/ConfirmModal';
import { useSpaceId } from "../hooks/useSpaceId";
import { usePermissions } from "../hooks/usePermissions";
import { useApproval } from '../context/ApprovalContext';
import { signalRService } from '../services/signalRService';
import { SIGNALR_EVENTS } from '../constants/signalREvents';
import { CONTENT_TYPES } from '../constants/contentTypes';

// 22 fixed global slots — id IS the slot number (1–22)
const POSITIONS = Array.from({ length: 22 }, (_, i) => ({ id: i + 1 }));

// Position 23 — fixed stadium entry spot, display-only (set directly in DB, no UI drag-drop)
const STADIUM_POSITION = { id: 23 };

// ── Status legend colours ────────────────────────────────────────────────────
const LEGEND = [
  { color: "#f59e0b", label: "Pending approval" },
  { color: "#ef4444", label: "Rejected — dismiss to clear" },
];

const TeleportMap = () => {
  const spaceId = useSpaceId();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/teleport-map', 'teleport-edit');
  const { isMySubmission } = useApproval();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { locale, t } = useLocalization();

  const [teleports, setTeleports] = useState([]);
  const [teleportTableId, setTeleportTableId] = useState(null);
  const [infoOpen, setInfoOpen]   = useState(false);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [snack, setSnack]         = useState({ open: false, message: "", severity: "success" });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, action: null, spotId: null, booth: null,
  });
  const [addButtonOpen, setAddButtonOpen] = useState(false);
  const [booths, setBooths] = useState([]);

  // ── Data load ────────────────────────────────────────────────────────────
  const loadData = useCallback(() => {
    if (!spaceId) return;
    getTeleportTable()
      .then((res) => {
        setTeleportTableId(res.id);
        const buttons = Array.isArray(res.buttons) ? res.buttons : res.data?.buttons ?? [];
        setTeleports(buttons);
      })
      .catch(console.error);
  }, [spaceId]);

  useEffect(() => { loadData(); }, [loadData]);

  // A map spot occupied by a booth must not be pickable for a new standalone teleport
  // button either — fetch booths fresh whenever the Add Button modal opens.
  useEffect(() => {
    if (!addButtonOpen) return;
    getBooths()
      .then((data) => setBooths(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [addButtonOpen]);

  // Auto-refresh when an approver approves/rejects a teleport button position change,
  // or when a submitter withdraws their pending proposal.
  useEffect(() => {
    const handle = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.TELEPORT || payload?.contentType === CONTENT_TYPES.TELEPORT_BUTTON) loadData();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handle);
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handle);
    return () => {
      signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handle);
      signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handle);
    };
  }, [loadData]);

  // ── Derived state ─────────────────────────────────────────────────────────
  // Build assignment map keyed on positionId (slot), not mapSpot.id
  const assignments = React.useMemo(() => {
    const map = {};
    for (const b of teleports) {
      if (b?.positionId) map[b.positionId] = b;
    }
    return map;
  }, [teleports]);

  // Map spots already tied to an existing teleport button (linked-to-booth or standalone).
  // Kept separate from the booth-inclusive set below — this one drives the "Link to Booth"
  // picker, which must only hide booths that already *have* a button, not every booth.
  const usedMapSpotIds = React.useMemo(
    () => new Set(teleports.map((b) => b.mapSpot?.id).filter(Boolean)),
    [teleports]
  );

  // Map spots occupied by ANY booth or teleport button — one map spot must never serve
  // two things at once, so the "Create New" spot picker checks across both.
  const usedMapSpotIdsForNewSpot = React.useMemo(
    () => new Set([
      ...booths.map((b) => b.mapSpotId),
      ...teleports.map((b) => b.mapSpot?.id),
    ].filter(Boolean)),
    [booths, teleports]
  );

  // Set of position IDs targeted by a *pending* approval from a different button
  const pendingTargetSpots = React.useMemo(() => {
    const set = new Set();
    for (const b of teleports) {
      if (b.approvalStatus === "pending" && b.proposedPositionId) {
        set.add(b.proposedPositionId);
      }
    }
    return set;
  }, [teleports]);

  // ── Confirmation flow ─────────────────────────────────────────────────────
  const confirmAction = (action, spotId, booth) => {
    // Block "remove" click when the booth already has a pending approval
    if (action === "remove" && booth?.approvalStatus === "pending") {
      setSnack({ open: true, message: "This button already has a pending approval. Wait for it to be resolved first.", severity: "warning" });
      return;
    }
    setConfirmDialog({ open: true, action, spotId, booth });
  };

  const handleConfirm = async () => {
    const { action, spotId, booth } = confirmDialog;
    setConfirmDialog({ open: false, action: null, spotId: null, booth: null });
    setSelectedBooth(null);

    const positionId = action === "remove" ? 0 : spotId;
    try {
      await submitButtonPositionChange(teleportTableId, booth.id, positionId);
      setSnack({ open: true, message: "Position change submitted for approval.", severity: "success" });
      await loadData();
    } catch (err) {
      const msg = err?.response?.data?.message
        ?? (err?.response?.status === 409
          ? "This button or spot already has a pending approval."
          : "Failed to submit. Please try again.");
      setSnack({ open: true, message: msg, severity: "error" });
    }
  };

  const handleCancel = () =>
    setConfirmDialog({ open: false, action: null, spotId: null, booth: null });

  // ── Dismiss (withdraw a rejected approval) ───────────────────────────────
  const handleDismiss = async (buttonId, approvalId) => {
    if (!approvalId) return;
    try {
      await withdrawApproval(approvalId);
      setSnack({ open: true, message: "Approval dismissed.", severity: "success" });
      await loadData();
    } catch {
      setSnack({ open: true, message: "Failed to dismiss approval.", severity: "error" });
    }
  };

  // ── Mobile booth selection ────────────────────────────────────────────────
  const handleBoothSelect = (booth) =>
    setSelectedBooth((prev) => (prev?.id === booth.id ? null : booth));

  const clearSelection = () => setSelectedBooth(null);

  // ── Map layout slices ─────────────────────────────────────────────────────
  const topSpots   = POSITIONS.slice(6, 16);
  const leftSpots  = POSITIONS.slice(0, 6).reverse();
  const rightSpots = POSITIONS.slice(16, 22);

  const getBoothLabel = (booth) =>
    booth?.localizedPairs?.values?.find((v) => v.localeId === locale)?.value ||
    booth?.localizedPairs?.values?.[0]?.value ||
    t(booth?.localizedPairs?.key, '') ||
    '';

  const selectedBoothLabel = selectedBooth ? getBoothLabel(selectedBooth) : "";

  // Unplaced booths = those with no current live position assignment
  const unplacedBooths = teleports.filter((b) => !b?.positionId);

  const renderBoothSpot = (spot) => (
    <BoothSpot
      key={spot.id}
      spot={spot}
      booth={assignments[spot.id]}
      onRequestChange={canEdit ? confirmAction : () => {}}
      isMobile={isMobile}
      selectedBooth={selectedBooth}
      isReserved={pendingTargetSpots.has(spot.id) && !assignments[spot.id]}
      onDismiss={canEdit ? (buttonId, aid) => isMySubmission(aid) && handleDismiss(buttonId, aid) : undefined}
    />
  );

  return (
    <ContentArea
      title="Teleport Map"
      icon={TeleportMapPng}
      container={false}
      titleAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setAddButtonOpen(true)}
              sx={{ fontWeight: 700, borderRadius: 2 }}
            >
              + Add Button
            </Button>
          )}
          <IconButton onClick={(e) => { e.currentTarget.blur(); setInfoOpen(true); }} sx={{ color: "#1e3a5f" }}>
            <InfoOutlinedIcon />
          </IconButton>
        </Box>
      }
    >
      {/* Status legend */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5, px: 1 }}>
        {LEGEND.map(({ color, label }) => (
          <Box key={color} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: color, border: "2px solid #fff", boxShadow: "0 0 0 1px #ccc" }} />
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Mobile selection hint bar */}
      {isMobile && selectedBooth && (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            bgcolor: "#0a326d", borderRadius: 2, px: 2, py: 1, mb: 1.5, gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <TouchAppIcon sx={{ color: "rgba(255,255,255,0.8)", fontSize: 18, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "white", fontWeight: 600, lineHeight: 1.3 }}>
              Tap a spot on the map to place "{selectedBoothLabel}"
            </Typography>
          </Box>
          <IconButton size="small" onClick={clearSelection} sx={{ color: "rgba(255,255,255,0.8)", flexShrink: 0, p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {isMobile && !selectedBooth && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: "text.secondary", textAlign: "center", mb: 1.5, fontStyle: "italic" }}
        >
          Tap a booth below to select it, then tap a spot on the map to place it.
        </Typography>
      )}

      {/* Scroll container */}
      <Box sx={{ p: 1, overflowX: "auto" }}>

        {/* Centering wrapper */}
        <Box sx={{ textAlign: "center" }}>

          {/* Stadium Layout */}
          <Box
            sx={{
              display: "inline-flex", flexDirection: "column", alignItems: "center",
              gap: { xs: 1, sm: 1.5, md: 2 },
              minWidth: { xs: 320, sm: 400, md: 480 },
            }}
          >
            {/* Top Row */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 0.75, sm: 1.5, md: 2 } }}>
              {topSpots.map(renderBoothSpot)}
            </Box>

            {/* Middle Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 2, md: 3 } }}>
                {leftSpots.map(renderBoothSpot)}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 2, md: 3 } }}>
                {rightSpots.map(renderBoothSpot)}
              </Box>
            </Box>

            {/* Position 23 — fixed stadium entry, no drag-drop */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 1, sm: 2 } }}>
              <BoothSpot
                spot={STADIUM_POSITION}
                booth={assignments[STADIUM_POSITION.id]}
                onRequestChange={() => {}}
                isFixed
              />
            </Box>

            {/* Stadium Text */}
            <Typography
              variant="h5" fontWeight="bold"
              sx={{
                mt: { xs: 2, sm: 4, md: 5 }, color: "white", backgroundColor: "#0a326d",
                pl: { xs: 3, sm: 6, md: 10 }, pr: { xs: 3, sm: 6, md: 10 },
                pt: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 },
                fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                borderRadius: "30px 30px 0px 0px",
              }}
            >
              STADIUM
            </Typography>
          </Box>
        </Box>

        {/* Placed booths (with status dot) + unplaced booths */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 4, flexWrap: "wrap" }}>
          {/* Placed booths that have a pending/rejected approval are also shown here so user can see status */}
          {teleports
            .filter((b) => b?.positionId && (b.approvalStatus === "pending" || b.approvalStatus === "rejected"))
            .map((booth) => (
              <DraggableIcon
                key={booth.id}
                icon={booth}
                isMobile={isMobile}
                isSelected={selectedBooth?.id === booth.id}
                onSelect={canEdit ? handleBoothSelect : () => {}}
                onDismiss={canEdit ? (buttonId, aid) => isMySubmission(aid) && handleDismiss(buttonId, aid) : undefined}
              />
            ))
          }
          {/* Unplaced booths */}
          {unplacedBooths.map((booth) => (
            <DraggableIcon
              key={booth.id}
              icon={booth}
              isMobile={isMobile}
              isSelected={selectedBooth?.id === booth.id}
              onSelect={canEdit ? handleBoothSelect : () => {}}
              onDismiss={canEdit ? (buttonId, aid) => isMySubmission(aid) && handleDismiss(buttonId, aid) : undefined}
            />
          ))}
        </Box>

        {/* Info Dialog */}
        <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Teleport Map Tour</DialogTitle>
          <DialogContent>
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 2 }}>
              <img src={TourGif} alt="Teleport Map Tour" style={{ maxWidth: "100%", height: "auto", borderRadius: 8 }} />
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Drag and drop a booth onto a map spot to propose a position change. The change goes to the approval inbox before it takes effect.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInfoOpen(false)} variant="contained" sx={{ bgcolor: "#0a326d" }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <ConfirmModal
          open={confirmDialog.open}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
          severity="warning"
          title={confirmDialog.action === "drop" ? "Submit Teleport Button Placement for Approval" : "Submit teleport for removal"}
          message={
            confirmDialog.action === "drop"
              ? `Submit a request to move "${getBoothLabel(confirmDialog.booth)}" to this spot?`
              : `Submit a request to remove "${getBoothLabel(confirmDialog.booth)}" from this spot?`
          }
          subtitle="This will go to the approval inbox. The map will not change until a manager approves it."
          confirmLabel={confirmDialog.action === "drop" ? "Submit for Approval" : "Submit for Removal"}
          cancelLabel="Cancel"
        />
      </Box>

      {/* Add Teleport Button Modal */}
      <AddTeleportButtonModal
        open={addButtonOpen}
        tableId={teleportTableId}
        usedMapSpotIds={usedMapSpotIds}
        usedMapSpotIdsForNewSpot={usedMapSpotIdsForNewSpot}
        onClose={() => setAddButtonOpen(false)}
        onSuccess={() => { setAddButtonOpen(false); loadData(); }}
      />

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </ContentArea>
  );
};

export default TeleportMap;
